/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import SystemMessage from "../../models/systemMessageModel";
import SystemMessages from "./systemMessages";
import {AppContext} from "../misc/appContext";
import {saveToLocal} from "../../util/storageUtil";
import {onConnectBolOauth} from "../../util/bolOauthUtil";
import IDate from '@dvrd/idate';
import {SELECTED_ACCOUNT_KEY} from "../../util/constants";
import ApiKey, {IGNORE_EXPIRY_FLEX_KEY} from "../../models/apiKeyModel";
import {showDialog, showSnackbar} from "@dvrd/dvr-controls";

export default function SystemMessagesController() {
    const context = useContext(AppContext);
    const {accounts} = context.accountContext;
    const [messages, setMessages] = useState<SystemMessage[]>([]);
    const [expiringAPIKeys, setExpiringAPIKeys] = useState<Array<ApiKey>>([]);

    function onConfirmDismissAPIExpiry(apiKey: ApiKey) {
        return function() {
            showDialog(
                <p>
                    Je kunt de waarschuwing voor het verlopen van deze koppeling uitschakelen. We zullen voor deze
                    koppeling dan geen melding meer tonen. Je zult nog wel automatische e-mails ontvangen over het
                    verlopen van jouw koppeling.<br/><br/>
                    Weet je zeker dat je deze melding niet meer wilt tonen voor deze koppeling?
                </p>,
                'Melding verbergen', ['Nee', {label: 'Ja', onClick: onDismissAPIExpiry(apiKey), primary: false}]
            )
        }
    }

    function onDismissAPIExpiry(apiKey: ApiKey) {
        return function() {
            apiKey.setFlex(IGNORE_EXPIRY_FLEX_KEY, true);
            apiKey.update((apiKey: ApiKey, success: boolean) => {
                if (!success) return
                const newAccounts = accounts.slice();
                for (let ix = 0; ix < newAccounts.length; ix++) {
                    const account = newAccounts[ix];
                    if (account.id === apiKey.accountId) {
                        account.replaceApiKey(apiKey);
                        newAccounts.splice(ix, 1, account);
                        context.onChangeContext({accounts: newAccounts});
                        break;
                    }
                }
                showSnackbar('De melding wordt niet meer getoond')
            });
        }
    }

    function onDismissMessage(messageId: string) {
        return function() {
            setMessages(messages.filter((message: SystemMessage) => message.id !== messageId));
        }
    }

    function onRenewApi(accountID: string) {
        return function(evt: React.MouseEvent) {
            saveToLocal(SELECTED_ACCOUNT_KEY, accountID);
            onConnectBolOauth(evt.shiftKey);
        }
    }

    function setApiExpiry() {
        const warnIfExpiresBefore = IDate.now().add(1, 'month');
        let expiring: Array<ApiKey> = [];
        for (const account of accounts) {
            expiring = expiring.concat(account.apiKeys.filter((apiKey: ApiKey) => {
                if (!apiKey.useOAuth || !apiKey.refreshExpiresAt || apiKey.getFlex(
                    IGNORE_EXPIRY_FLEX_KEY)) return false;
                return apiKey.refreshExpiresAt.isAtOrBefore(warnIfExpiresBefore, 'day');
            }));
        }
        setExpiringAPIKeys(expiring);
    }

    useEffect(() => {
        SystemMessage.getAll((messages: SystemMessage[]) => {
            setMessages(messages);
        })
    }, []);

    useEffect(() => {
        setApiExpiry();
    }, [accounts]);

    return (
        <SystemMessages onDismissMessage={onDismissMessage} onRenewApi={onRenewApi}
                        onDismissAPIExpiry={onConfirmDismissAPIExpiry} messages={messages}
                        expiringAPIKeys={expiringAPIKeys}/>
    )
}